import { useCallback, useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";
import { Product } from "~/types/product";
import { isPickupAddress } from "~/utils/addressUtils";
import {
  FLETE_PRICE,
  FLETE_PRODUCT_ID,
  FLETE_PRODUCT_SHORT_NAME,
  requiresFlete,
} from "~/utils/fleteUtils";

interface FleteProductData {
  id: string;
  short_name: string;
  long_name: string | null;
  net_price: number | null;
  suggested_price: number | null;
}

interface UseFleteProductReturn {
  fleteProduct: Product | null;
  isLoading: boolean;
  fleteRequired: boolean;
  addFleteToOrder: () => void;
}

interface UseFleteProductProps {
  selectedAddressId: string | null;
  deliveryState: string | null | undefined;
  orderItems: { product: { id: string } }[];
  handleAddProduct: (
    product: Product,
    quantity: number,
    unitPrice: number,
    suggestedPrice: number,
    hasPriceRanges: boolean,
    discountPercent: number
  ) => void;
}

export const useFleteProduct = ({
  selectedAddressId,
  deliveryState,
  orderItems,
  handleAddProduct,
}: UseFleteProductProps): UseFleteProductReturn => {
  const [fleteProduct, setFleteProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch FLETE product details on mount
  useEffect(() => {
    const fetchFleteProduct = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("id", FLETE_PRODUCT_ID)
          .single();

        if (error) {
          console.error("Error fetching FLETE product:", error);
          return;
        }

        if (data) {
          setFleteProduct({
            id: data.id,
            short_name: data.short_name,
            long_name: data.long_name || "",
            description: data.description || "",
            stock: 999999, // FLETE has unlimited "stock"
            product_image_url: data.product_image_url || "",
            created_at: data.created_at,
            updated_at: data.updated_at,
            deleted_at: data.deleted_at,
            barcode: data.barcode || undefined,
            unit_code: data.unit_code || undefined,
            base_measurement_unit: data.base_measurement_unit || undefined,
            group_code: data.group_code || undefined,
            group_description: data.group_description || undefined,
            subgroup_code: data.subgroup_code || undefined,
            subgroup_description: data.subgroup_description || undefined,
            net_price: data.net_price || FLETE_PRICE,
            suggested_price: data.suggested_price || FLETE_PRICE,
          });
        }
      } catch (err) {
        console.error("Error in fetchFleteProduct:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFleteProduct();
  }, []);

  // Determine if FLETE is required based on address
  const fleteRequired =
    !isPickupAddress(selectedAddressId) && requiresFlete(deliveryState);

  // Check if FLETE is already in order
  const hasFleteInOrder = orderItems.some(
    (item) => item.product.id === FLETE_PRODUCT_ID
  );

  // Add FLETE to order
  const addFleteToOrder = useCallback(() => {
    if (!fleteProduct || hasFleteInOrder) return;

    const price = fleteProduct.net_price || FLETE_PRICE;

    handleAddProduct(
      fleteProduct,
      1, // quantity
      price, // unitPrice
      fleteProduct.suggested_price || price, // suggestedPrice
      false, // hasPriceRanges
      0 // discountPercent
    );
  }, [fleteProduct, hasFleteInOrder, handleAddProduct]);

  return {
    fleteProduct,
    isLoading,
    fleteRequired,
    addFleteToOrder,
  };
};
